import { css, LitElement, unsafeCSS } from 'lit';
import mainStyles from './main.scss?inline';
import { property, query } from 'lit/decorators.js';
import { Variant } from './types';
import { LocaleCode } from '@dertour/common-lib';
import { localized, updateLocale } from '../../localization/config';
import { renderAppointmentBookingFormTemplate } from '../../templates/appointment-booking-form';
import type { FormAlert } from '../../types/appointment-booking.types';

@localized()
export class RequestForm extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    static formAssociated = true;

    protected _internals: ElementInternals;

    private readonly emailPattern: string = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

    private readonly namePattern: string = "^(?!\\s+$)[A-Za-zÀ-ÖØ-öø-ÿ'\\- ]+$";

    @property({ type: String })
    variant: Variant = 'default';

    @property({ type: String })
    lang = LocaleCode.DE_CH;

    @property({ type: String })
    newsletter = '';

    @property({ type: String, attribute: 'data-protection-link' })
    dataProtectionLink = '';

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ attribute: false })
    public formAlert?: FormAlert;

    @query('form') formElement?: HTMLFormElement;

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await updateLocale(this.lang || LocaleCode.DE_CH);
    }

    override async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        if (changedProperties.has('lang')) {
            await updateLocale(this.lang || LocaleCode.DE_CH);
        }
    }

    error(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.error', { detail, bubbles: true, composed: true }));
    }

    protected emitEvent(name: string, detail: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    protected warn(name: string, data: object): void {
        const detail: { name: string; data: object } = { name: name.toLowerCase(), data };

        this.dispatchEvent(new CustomEvent('der.warning', { detail, bubbles: true, composed: true }));
    }

    setDERValidity(flags?: ValidityStateFlags, message?: string, anchor?: HTMLElement) {
        const currentValid = this._internals.validity.valid;
        this._internals.setValidity(flags, message, anchor);
        const newValid = this._internals.validity.valid;

        if (currentValid !== newValid) {
            this.emitEvent('der.validity-change', {
                validity: this._internals.validity,
                validationMessage: message,
                element: anchor || this,
            });
        }
    }

    getFormValue(): Record<string, FormDataEntryValue> {
        return Object.fromEntries(new FormData(this.formElement));
    }

    onSubmit(e: Event): void {
        e.preventDefault();
        this.emitEvent('der.submit', this.getFormValue());
    }

    validate(): boolean {
        if (this.formElement!.checkValidity()) {
            return true;
        }
        const invalidEl = this.formElement!.querySelector(':invalid') as HTMLElement | null;
        invalidEl?.scrollIntoView({ block: 'center', behavior: 'auto' });
        window.setTimeout(() => {
            this.formElement!.reportValidity();
        }, 300);
        return false;
    }

    private _checkFormValidity(): void {
        if (!this.formElement) {
            return;
        }
        const isValid = this.formElement?.checkValidity();
        this.setDERValidity({ badInput: !isValid }, 'Booking Appointment Form Validation', this.formElement);
    }

    override render() {
        return renderAppointmentBookingFormTemplate({
            variant: this.variant,
            lang: this.lang,
            newsletter: this.newsletter,
            dataProtectionLink: this.dataProtectionLink,
            namePattern: this.namePattern,
            emailPattern: this.emailPattern,
            formAlert: this.formAlert,
            onValidityChange: this._checkFormValidity.bind(this),
            onSubmit: this.onSubmit.bind(this),
        });
    }
}
